import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { formatCurrency, formatNumber } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom marker icons
const createCustomIcon = (color) => {
  return L.divIcon({
    className: "custom-marker",
    html: `
      <div style="
        width: 24px;
        height: 24px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        animation: pulse 2s infinite;
      "></div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
};

const statusColors = {
  active: "#6B21A8",
  funded: "#F59E0B",
  in_progress: "#8B5CF6",
  completed: "#22C55E",
};

// Map bounds auto-fit component
const FitBounds = ({ projects }) => {
  const map = useMap();
  
  useEffect(() => {
    if (projects && projects.length > 0) {
      const bounds = projects
        .filter((p) => p.latitude && p.longitude)
        .map((p) => [p.latitude, p.longitude]);
      
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 5 });
      }
    }
  }, [projects, map]);
  
  return null;
};

export const GlobalMap = ({ projects = [], height = "500px" }) => {
  const [mapReady, setMapReady] = useState(false);

  // Always use light CartoDB Positron tiles
  const tileUrl = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

  return (
    <div
      className="rounded-lg overflow-hidden border border-border"
      style={{ height }}
      data-testid="global-map"
    >
      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height: "100%", width: "100%" }}
        whenReady={() => setMapReady(true)}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
          url={tileUrl}
        />
        
        {mapReady && <FitBounds projects={projects} />}
        
        {projects.map((project) => {
          if (!project.latitude || !project.longitude) return null;
          
          const icon = createCustomIcon(statusColors[project.status] || "#6B21A8");
          const progress = (project.current_amount / project.target_amount) * 100;
          
          return (
            <Marker
              key={project.id}
              position={[project.latitude, project.longitude]}
              icon={icon}
            >
              <Popup>
                <div className="min-w-[240px] p-2" data-testid={`map-popup-${project.id}`}>
                  <h3 className="font-heading font-bold text-sm mb-1">{project.title}</h3>
                  <p className="text-xs text-muted-foreground mb-2">{project.country}</p>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span>Progress</span>
                      <span className="font-medium">{progress.toFixed(0)}%</span>
                    </div>
                    <Progress value={progress} className="h-1.5" />
                    
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Raised</span>
                      <span className="font-medium">{formatCurrency(project.current_amount)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Goal</span>
                      <span>{formatCurrency(project.target_amount)}</span>
                    </div>
                    
                    {project.beneficiaries_reached > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Beneficiaries</span>
                        <span>{formatNumber(project.beneficiaries_reached)}</span>
                      </div>
                    )}
                    
                    <Badge
                      variant="outline"
                      className="text-xs mt-2"
                      style={{ borderColor: statusColors[project.status] }}
                    >
                      {project.status?.replace("_", " ")}
                    </Badge>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      
      {/* Map Legend */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg p-3 border border-border shadow-sm">
        <p className="text-xs font-medium mb-2">Project Status</p>
        <div className="space-y-1">
          {Object.entries(statusColors).map(([status, color]) => (
            <div key={status} className="flex items-center gap-2 text-xs">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="capitalize">{status.replace("_", " ")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GlobalMap;
